import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Download, LogOut, Search } from "lucide-react";
import { toast } from "sonner";
import {
  fetchLeads,
  fetchLead,
  updateLeadStatus,
  deleteLead,
  fetchStats,
  exportXlsxUrl,
  resendLeadEmail,
} from "../lib/api";
import { tokenStorage } from "../lib/tokenStorage";
import Logo from "../components/Logo";
import { STATUS_LABEL } from "../components/admin/leadStatus";
import { LeadsTable } from "../components/admin/LeadsTable";
import { LeadDetailDrawer } from "../components/admin/LeadDetailDrawer";

export default function AdminDashboard() {
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedLead, setSelectedLead] = useState(null);
  const [resending, setResending] = useState(false);

  const token = tokenStorage.get();

  useEffect(() => {
    document.title = "Tableau de bord Admin — GMC Rénovation Façade";
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex, nofollow");
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([fetchLeads(), fetchStats()]);
      setLeads(l);
      setStats(s);
    } catch (e) {
      if (e?.response?.status === 401) {
        tokenStorage.clear();
        nav("/admin/login");
      } else {
        toast.error("Erreur de chargement");
      }
    } finally {
      setLoading(false);
    }
  }, [nav]);

  useEffect(() => {
    if (!token) {
      nav("/admin/login");
      return;
    }
    refresh();
  }, [token, nav, refresh]);

  const logout = () => {
    tokenStorage.clear();
    nav("/admin/login");
  };

  const exportXlsx = async () => {
    try {
      const res = await fetch(exportXlsxUrl(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("export error");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `leads_gmc_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export Excel téléchargé");
    } catch (e) {
      toast.error("Erreur d'export");
    }
  };

  const openLead = async (id) => {
    try {
      const full = await fetchLead(id);
      setSelectedLead(full);
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const setStatus = async (id, status) => {
    try {
      await updateLeadStatus(id, status);
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      setSelectedLead((sel) => (sel?.id === id ? { ...sel, status } : sel));
      toast.success("Statut mis à jour");
      const s = await fetchStats();
      setStats(s);
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const removeLead = async (id) => {
    if (!window.confirm("Supprimer ce lead ?")) return;
    await deleteLead(id);
    setLeads((ls) => ls.filter((l) => l.id !== id));
    setSelectedLead(null);
    toast.success("Lead supprimé");
  };

  const resendEmail = async (id) => {
    setResending(true);
    try {
      await resendLeadEmail(id);
      toast.success("Email renvoyé à gmcrenov@icloud.com");
      setLeads((ls) =>
        ls.map((l) => (l.id === id ? { ...l, email_sent: true } : l))
      );
      setSelectedLead((sel) =>
        sel?.id === id ? { ...sel, email_sent: true } : sel
      );
    } catch (e) {
      toast.error("Échec de l'envoi email. Réessayez plus tard.", {
        duration: 8000,
      });
    } finally {
      setResending(false);
    }
  };

  const filtered = leads.filter((l) => {
    if (statusFilter !== "all" && l.status !== statusFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      l.name?.toLowerCase().includes(q) ||
      l.email?.toLowerCase().includes(q) ||
      l.city?.toLowerCase().includes(q) ||
      l.phone?.toLowerCase().includes(q)
    );
  });

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <div className="border-b border-white/5 sticky top-0 bg-black/80 backdrop-blur-xl z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-3">
              <Logo className="h-10 w-auto object-contain" alt="GMC" />
              <div className="hidden sm:block">
                <div className="font-display text-base leading-none">Admin</div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                  Dashboard
                </div>
              </div>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportXlsx}
              data-testid="admin-export-xlsx"
              className="btn-ghost text-sm py-2"
            >
              <Download size={14} /> Excel
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="btn-ghost text-sm py-2"
            >
              <LogOut size={14} /> Déconnexion
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <h1 className="font-display text-4xl md:text-5xl font-light mb-2">
          Tableau de bord
        </h1>
        <p className="text-neutral-400 mb-10">
          Suivez et gérez les demandes de devis en temps réel.
        </p>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10" data-testid="admin-stats">
          <StatCard label="Total leads" value={stats?.total ?? "—"} accent />
          <StatCard label="Nouveaux" value={stats?.new ?? "—"} />
          <StatCard label="Contactés" value={stats?.contacted ?? "—"} />
          <StatCard label="Gagnés" value={stats?.won ?? "—"} />
          <StatCard label="7 derniers jours" value={stats?.last7 ?? "—"} />
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <div className="flex-1 relative">
            <Search
              size={16}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500"
            />
            <input
              data-testid="admin-search-input"
              placeholder="Rechercher nom, email, ville…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-[#0d0d0d] border border-white/10 rounded-full pl-11 pr-4 py-3 text-sm outline-none focus:border-[#FF5A00] transition-colors"
            />
          </div>
          <div className="flex gap-2">
            {["all", "new", "contacted", "won", "lost"].map((s) => (
              <button
                key={s}
                data-testid={`admin-filter-${s}`}
                onClick={() => setStatusFilter(s)}
                className={`pill ${statusFilter === s ? "active" : ""}`}
              >
                {s === "all" ? "Tous" : STATUS_LABEL[s]}
              </button>
            ))}
          </div>
        </div>

        <LeadsTable
          loading={loading}
          leads={filtered}
          onOpen={openLead}
          onDelete={removeLead}
        />
      </div>

      <LeadDetailDrawer
        lead={selectedLead}
        onClose={() => setSelectedLead(null)}
        onSetStatus={setStatus}
        onResend={resendEmail}
        resending={resending}
      />
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div
      className={`rounded-2xl border p-5 ${accent ? "border-[#FF5A00]/30 bg-[#FF5A00]/[0.05]" : "border-white/10 bg-[#0a0a0a]"}`}
    >
      <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2">{label}</div>
      <div className="font-display text-3xl text-white">{value}</div>
    </div>
  );
}
